import { create } from "zustand";
import { api } from "../utils/axios-instance";
import { useStorageStore } from "./storageStore";

export const useFolderStore = create((set, get) => ({
  // State
  folders: [],
  recycleFolders: [],

  loading: false,
  recycleLoading: false,

  refreshFolders: false,
  refreshRecycleFolders: false,

  selectedFolders: [],

  anchorEl: null,

  // Setters
  setFolders: (folders) => set({ folders }),

  setRecycleFolders: (folders) => set({ recycleFolders: folders }),

  setLoading: (loading) => set({ loading }),

  setRecycleLoading: (loading) => set({ recycleLoading: loading }),

  setRefreshFolders: (value) => set({ refreshFolders: value }),

  setRefreshRecycleFolders: (value) => set({ refreshRecycleFolders: value }),

  setSelectedFolders: (folders) => set({ selectedFolders: folders }),

  setAnchorEl: (anchorEl) => set({ anchorEl }),

  // Dashboard Folder Operations
  fetchFolders: async () => {
    set({ loading: true });

    try {
      const response = await api.get("/file/folder");

      set({
        folders: response.data || [],
      });
    } catch (error) {
      console.error("Failed to fetch folders", error);
    } finally {
      set({
        loading: false,
        refreshFolders: false,
      });
    }
  },

  removeFolder: (folderId) => {
    set((state) => ({
      folders: state.folders.filter((folder) => folder.id !== folderId),
      anchorEl: null,
    }));
  },

  restoreFolderLocally: (folderId) => {
    set((state) => ({
      recycleFolders: state.recycleFolders.filter(
        (folder) => folder.id !== folderId
      ),
    }));
  },

  // Recycle Bin Operations
  fetchRecycleFolders: async () => {
    set({
      recycleLoading: true,
    });

    try {
      const response = await api.get("/file/recycle-bin-folder");

      set({
        recycleFolders: response.data || [],
      });
    } catch (error) {
      console.error("Failed to fetch recycle folders", error);
    } finally {
      set({
        recycleLoading: false,
        refreshRecycleFolders: false,
      });
    }
  },

  recycleFolder: async (folderId) => {
    // Grab the folder's cached size before it disappears from local state —
    // the backend releases these bytes from org/user storage as part of
    // this call, so reflect it in the sidebar immediately instead of
    // waiting for the next poll.
    const folder = get().folders.find((f) => f.id === folderId);
    const folderSize = folder?.size_bytes || 0;

    await api.post(`/file/recycle-folder/${folderId}/`);

    get().removeFolder(folderId);

    if (folderSize > 0) {
      useStorageStore.getState().adjustLocal(-folderSize);
    }

    set({
      refreshRecycleFolders: true,
    });
  },

  restoreFolder: async (folderId) => {
    // Mirror of recycleFolder: the backend re-adds these bytes to org/user
    // storage on restore, so bump the sidebar back up immediately.
    const folder = get().recycleFolders.find((f) => f.id === folderId);
    const folderSize = folder?.size_bytes || 0;

    await api.delete(`/file/restore-folder/${folderId}/`);

    get().restoreFolderLocally(folderId);

    if (folderSize > 0) {
      useStorageStore.getState().adjustLocal(folderSize);
    }

    set({
      refreshFolders: true,
    });
  },

  deleteFolderForever: async (folderId) => {
    // No storage adjustment here — the bytes were already released from
    // org/user storage when the folder was recycled. Permanently deleting
    // it is just cleanup, not a change in usage.
    await api.delete(`/file/delete-folder/${folderId}/`);

    get().restoreFolderLocally(folderId);
  },

  // Multi Select
  toggleFolderSelection: (folder) => {
    const selected = get().selectedFolders;

    const exists = selected.some((f) => f.id === folder.id);

    if (exists) {
      set({
        selectedFolders: selected.filter((f) => f.id !== folder.id),
      });
    } else {
      set({
        selectedFolders: [...selected, folder],
      });
    }
  },

  clearSelection: () =>
    set({
      selectedFolders: [],
    }),
}));
